import { useMemo, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useData } from "@/contexts/DataContext";
import { useProjects } from "@/hooks/useProjects";
import { useWebSocket } from "@/contexts/Websocket/WebSocketProvider";
import { useExecutionRecovery } from "@/hooks/useExecutionRecovery";
import { isExecutionActive } from "../../redux/reducers/Execution";
import { reducerType } from "../../redux/reducers";
import { AppDispatch } from "../../redux/store";
import ChatInput from "./ChatInput";
import {
  ChatHeader,
  ChatMessageList,
  CollapsedChatPanel,
  useAutoScrollChat,
  useChatStreamingRefs,
  useExecutionMessageSync,
  useLoadConversationHistory,
  useNewProjectInit,
  useProjectBoundaryReset,
  useSendChatMessage,
} from "./chat";

interface IDEChatPanelProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onHelpClick?: () => void;
}

/**
 * Top-level chat panel for the IDE view.
 *
 * This component is intentionally thin: it wires together a collection of
 * single-responsibility hooks (project boundary reset, conversation history
 * loading, new-project init, execution-to-message sync, send-message,
 * auto-scroll) and renders presentational sub-components. All logic that
 * was previously inlined here lives in `./chat/`.
 */
const IDEChatPanel = ({
  isCollapsed,
  onToggleCollapse,
  onHelpClick,
}: IDEChatPanelProps) => {
  const { projectId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const ws = useWebSocket();
  const { getProjectById } = useProjects();

  const {
    conversations,
    currentConversationId,
    isLoadingConversation,
    addMessage,
    createConversation,
    updateMessage,
    getConversationsByProjectId,
    switchConversation,
    clearCurrentConversation,
    loadConversationHistory,
  } = useData();

  // Execution state from Redux (driven by WebSocket messages)
  const executionState = useSelector((state: reducerType) => state.Execution);

  // Polls backend on tab focus / staleness to recover from missed
  // WebSocket completions.
  useExecutionRecovery(projectId);

  // Bundle of refs tracking in-flight streaming state. Shared across
  // hooks so they all reference the same source of truth.
  const refs = useChatStreamingRefs();

  // Project-boundary handling: snapshot navigation state and reset
  // local + Redux execution state on every projectId change.
  const navStateRef = useProjectBoundaryReset({
    projectId,
    location,
    dispatch,
    clearCurrentConversation,
    refs,
  });

  // Load existing conversation history from the API for non-new projects.
  useLoadConversationHistory({
    projectId,
    navStateRef,
    getConversationsByProjectId,
    switchConversation,
    loadConversationHistory,
  });

  // Derived: current conversation + messages.
  const currentConversation = useMemo(
    () => conversations.find((conv) => conv.id === currentConversationId),
    [conversations, currentConversationId],
  );
  const messages = useMemo(
    () => currentConversation?.messages || [],
    [currentConversation],
  );

  // New-project init: seed the conversation with the user's HomePage prompt
  // and an AI placeholder once we mount.
  useNewProjectInit({
    projectId,
    navStateRef,
    messageCount: messages.length,
    currentConversationId,
    createConversation,
    addMessage,
    refs,
  });

  // Sync Redux Execution slice → chat messages (streaming, segments,
  // completion, errors, IDLE safety net).
  useExecutionMessageSync({
    executionState,
    currentConversationId,
    refs,
    addMessage,
    updateMessage,
    dispatch,
  });

  // Auto-scroll to the bottom on new messages and streaming updates,
  // unless the user has manually scrolled up.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useAutoScrollChat(scrollContainerRef, messages, {
    attachKey: isCollapsed,
    resetKey: projectId,
  });

  // Whether the execution is currently active (sending/queued/in-progress).
  // Used to block new submissions and to render the working indicator.
  const executionActive = isExecutionActive(executionState.status);

  const handleSendMessage = useSendChatMessage({
    projectId,
    ws,
    dispatch,
    executionState,
    navStateRef,
    refs,
    messageCount: messages.length,
    currentConversationId,
    createConversation,
    addMessage,
    getProjectById,
  });

  if (isCollapsed) {
    return <CollapsedChatPanel onToggleCollapse={onToggleCollapse} />;
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <ChatHeader onToggleCollapse={onToggleCollapse} />

      <ChatMessageList
        ref={scrollContainerRef}
        messages={messages}
        isLoadingConversation={isLoadingConversation}
        showWorkingIndicator={
          executionActive && !refs.streamingMessageIdRef.current
        }
      />

      {/* Chat Input */}
      <div className="[&_.max-w-4xl]:max-w-none">
        <ChatInput
          placeholder={
            messages.length > 0
              ? "Ask a follow-up question..."
              : "Describe a new task"
          }
          onMessageSend={(message, _features, attachments) =>
            handleSendMessage(message, attachments)
          }
          disabled={executionActive}
          // Existing project: attachments upload under the current projectId.
          resolveUploadProjectId={projectId ? async () => projectId : undefined}
          onHelpClick={onHelpClick}
        />
      </div>
    </div>
  );
};

export default IDEChatPanel;
